/**
 * MD-29 Admin Content Management - F-A-CONTENT-MGMT.
 *
 * Trang `/dashboard/content` cho founder/admin xem trang thai:
 *   - Tab Blog: 36+ article tu pages/public/content-manifest.json (qua API)
 *   - Tab Social (FB): post FB Page Asklaw tu /api/v1/admin/social/posts (reuse MD-26)
 *
 * Phuong an C - hybrid view + state tracking. Phase 1 read-only.
 *
 * Spec: specs/domains/admin/F-A-CONTENT-MGMT.md
 * Decision: DECISIONS s2026-05-12-CONTENT-MGMT-MD-29-PLAN-C
 */

import { useMemo, useState } from 'react';
import { DashboardGate } from './DashboardGate';
import { DashboardLayout } from './DashboardSidebar';
import {
  useAdminContent,
  useFbPosts,
  type BlogListResponse,
  type ChannelsResponse,
  type BlogItem,
  type PromotedChannel,
} from './use-admin-content';
import { SocialInsightPanel } from './SocialInsightPanel';
import { SocialConnectCards } from './SocialConnectCards';
import { SocialAnalyticsPanel } from './SocialAnalyticsPanel';

type TabId = 'blog' | 'social';
type SocialSubTab = 'connect' | 'posts' | 'analytics';

function StatusBadge({ status }: { status: string }) {
  const label =
    status === 'published'
      ? 'Da xuat ban'
      : status === 'draft'
      ? 'Ban thao'
      : status === 'review'
      ? 'Cho duyet'
      : status === 'archived'
      ? 'Da luu tru'
      : status;
  return <span className={`content-status-pill content-status-pill-${status}`}>{label}</span>;
}

function UplBadge({ item }: { item: BlogItem }) {
  if (!item.upl_warning) {
    return <span className="content-upl-pill content-upl-pill-ok">OK</span>;
  }
  const hits = item.upl_hits.map((h) => `${h.id}(${h.count})`).join(', ');
  return (
    <span
      className="content-upl-pill content-upl-pill-warning"
      title={`Phat hien cum tu UPL drift: ${hits}. Kiem tra lai truoc khi tai xuat ban.`}
    >
      Canh bao
    </span>
  );
}

function DisclaimerBadge({ item }: { item: BlogItem }) {
  if (item.disclaimer_present) {
    return (
      <span className="content-disc-pill content-disc-pill-ok" title="Du dieu khoan tu choi trach nhiem">
        Du
      </span>
    );
  }
  return (
    <span
      className="content-disc-pill content-disc-pill-warn"
      title={`Chi co ${item.disclaimer_marker_hits}/5 marker. Can them disclaimer block.`}
    >
      Thieu
    </span>
  );
}

/**
 * Patch 2026-05-12 MD-29 - badge cot "Da day social".
 * Spec F-A-CONTENT-MGMT.md AC-13.
 */
function PromotedBadges({ channels }: { channels: PromotedChannel[] | undefined }) {
  if (!channels || channels.length === 0) {
    return (
      <span className="content-promoted-pill content-promoted-pill-none" title="Chua day kenh nao">
        -
      </span>
    );
  }
  // Phase 0 chi co channel 'facebook' nen render don gian. Adapter-ready cho channel sau.
  // Neu cung 1 slug co nhieu post, lay status uu tien published > scheduled.
  const fbPosts = channels.filter((c) => c.channel === 'facebook');
  if (fbPosts.length === 0) {
    return <span className="content-promoted-pill content-promoted-pill-none">-</span>;
  }
  const hasPublished = fbPosts.find((c) => c.status === 'published');
  const best = hasPublished ?? fbPosts[0];
  const label = best.status === 'published' ? 'Da dang' : 'Hen gio';
  const cls = best.status === 'published' ? 'content-promoted-pill-published' : 'content-promoted-pill-scheduled';
  const when = best.posted_at ?? best.scheduled_at ?? '';
  const tooltip = `FB ${label}${when ? ' luc ' + when : ''}${fbPosts.length > 1 ? ' (+' + (fbPosts.length - 1) + ' post khac)' : ''}`;
  const inner = (
    <span className={`content-promoted-pill ${cls}`} title={tooltip}>
      FB - {label}
    </span>
  );
  if (best.post_url) {
    return (
      <a href={best.post_url} target="_blank" rel="noreferrer" className="content-promoted-link">
        {inner}
      </a>
    );
  }
  return inner;
}

function fmtDate(d: string | null): string {
  if (!d) return '-';
  return d;
}

function fmtNum(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(Math.round(n));
}

function fmtPercent(x: number | undefined | null): string {
  if (x == null) return '-';
  return `${(x * 100).toFixed(1)}%`;
}

function fmtAge(ts: number): string {
  if (!ts) return '-';
  const ageSec = Math.max(0, Math.floor(Date.now() / 1000) - ts);
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)} phut truoc`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)} gio truoc`;
  return `${Math.floor(ageSec / 86400)} ngay truoc`;
}

// ---------- Blog tab ----------

function BlogTab() {
  const [cluster, setCluster] = useState('');
  const [lang, setLang] = useState('');
  const [status, setStatus] = useState('');
  const [audience, setAudience] = useState('');
  const [search, setSearch] = useState('');
  // Patch 2026-05-12 MD-29 - filter "Trang thai FB" (AC-15).
  const [fbStatus, setFbStatus] = useState<'all' | 'published' | 'scheduled' | 'none'>('all');

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (cluster) p.set('cluster', cluster);
    if (lang) p.set('lang', lang);
    if (status) p.set('status', status);
    if (audience) p.set('audience', audience);
    if (search.trim()) p.set('q', search.trim());
    const s = p.toString();
    return s ? `?${s}` : '';
  }, [cluster, lang, status, audience, search]);

  const { data, loading, error, refetch } = useAdminContent<BlogListResponse>(`/blog${query}`);

  const clusters = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.summary.by_cluster ?? {}).sort();
  }, [data]);

  const onClear = () => {
    setCluster('');
    setLang('');
    setStatus('');
    setAudience('');
    setSearch('');
    setFbStatus('all');
  };

  // Patch 2026-05-12 MD-29 (AC-15): client-side filter theo "Trang thai FB".
  const visibleItems = useMemo(() => {
    if (!data) return [];
    if (fbStatus === 'all') return data.items;
    return data.items.filter((it) => {
      const ch = it.promoted_channels ?? [];
      const fb = ch.filter((c) => c.channel === 'facebook');
      if (fbStatus === 'none') return fb.length === 0;
      if (fbStatus === 'published') return fb.some((c) => c.status === 'published');
      if (fbStatus === 'scheduled') return fb.some((c) => c.status === 'scheduled');
      return true;
    });
  }, [data, fbStatus]);

  return (
    <section className="content-mgmt-tab-body">
      <div className="content-mgmt-filter-bar">
        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Chu de</span>
          <select value={cluster} onChange={(e) => setCluster(e.target.value)}>
            <option value="">Tat ca chu de</option>
            {clusters.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Ngon ngu</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)}>
            <option value="">Tat ca</option>
            <option value="vi">Tieng Viet</option>
            <option value="en">Tieng Anh</option>
          </select>
        </label>

        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Trang thai</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Tat ca trang thai</option>
            <option value="published">Da xuat ban</option>
            <option value="draft">Ban thao</option>
            <option value="review">Cho duyet</option>
            <option value="archived">Da luu tru</option>
          </select>
        </label>

        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Doi tuong</span>
          <select value={audience} onChange={(e) => setAudience(e.target.value)}>
            <option value="">Tat ca</option>
            <option value="vn-main">Nguoi VN</option>
            <option value="expat-en">Expat EN</option>
            <option value="ve-asklaw">Ve Asklaw</option>
          </select>
        </label>

        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Trang thai FB</span>
          <select value={fbStatus} onChange={(e) => setFbStatus(e.target.value as typeof fbStatus)}>
            <option value="all">Tat ca</option>
            <option value="published">Da dang</option>
            <option value="scheduled">Hen gio</option>
            <option value="none">Chua day</option>
          </select>
        </label>

        <label className="content-mgmt-filter content-mgmt-filter-search">
          <span className="content-mgmt-filter-label">Tìm kiếm</span>
          <input
            type="search"
            value={search}
            placeholder="Tim theo tieu de hoac tu khoa..."
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        <button type="button" className="content-mgmt-clear-btn" onClick={onClear}>
          Xoa loc
        </button>

        <button type="button" className="content-mgmt-refresh-btn" onClick={() => void refetch()}>
          Tai lai
        </button>
      </div>

      {loading && <p className="content-mgmt-loading">Dang tai noi dung...</p>}
      {error && <p className="content-mgmt-error">Loi tai du lieu: {error}</p>}
      {!loading && !error && data && (
        <>
          <div className="content-mgmt-summary">
            Hien {visibleItems.length}/{data.total_all} bai. Manifest cập nhật lúc {data.generated_at.slice(0, 19).replace('T', ' ')} UTC.
            {` `}Disclaimer day du: {data.summary.disclaimer_present_count}/{data.total_all}. UPL canh bao:{' '}
            {data.summary.upl_warning_count}/{data.total_all}.
            {data._promoted_meta && (
              <>
                {' '}FB post da map:{' '}
                {data._promoted_meta.total_fb_rows - data._promoted_meta.orphan_fb_count}/
                {data._promoted_meta.total_fb_rows}
                {data._promoted_meta.orphan_fb_count > 0 && (
                  <> ({data._promoted_meta.orphan_fb_count} post khong khop blog slug)</>
                )}
                {data._promoted_meta.served_from_cache && <> - cache hit</>}
              </>
            )}
          </div>

          {visibleItems.length === 0 ? (
            <p className="content-mgmt-empty">Khong co bai viet nao khop bo loc. Thu xoa loc de xem tat ca.</p>
          ) : (
            <div className="content-mgmt-table-wrap">
              <table className="content-mgmt-table">
                <thead>
                  <tr>
                    <th>Tieu de</th>
                    <th>Chu de</th>
                    <th>Ngon ngu</th>
                    <th>Trang thai</th>
                    <th>UPL</th>
                    <th>Disclaimer</th>
                    <th>Trich dan</th>
                    <th>Da day social</th>
                    <th>Ngay dang</th>
                    <th>Hanh dong</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((it) => (
                    <tr key={it.slug}>
                      <td className="content-mgmt-col-title">
                        <a
                          href={it.slug.startsWith('/') ? it.slug : `/blog/${it.slug}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {it.title}
                        </a>
                        {it.primary_keyword && (
                          <span className="content-mgmt-keyword">{it.primary_keyword}</span>
                        )}
                      </td>
                      <td>{it.cluster}</td>
                      <td>{it.lang === 'vi' ? 'VN' : it.lang === 'en' ? 'EN' : it.lang}</td>
                      <td>
                        <StatusBadge status={it.status} />
                      </td>
                      <td>
                        <UplBadge item={it} />
                      </td>
                      <td>
                        <DisclaimerBadge item={it} />
                      </td>
                      <td>{it.citations_count}</td>
                      <td>
                        <PromotedBadges channels={it.promoted_channels} />
                      </td>
                      <td>{fmtDate(it.publish_date)}</td>
                      <td>
                        <a
                          href={`https://github.com/asklaw/co-law/blob/main/pages/src/content/blog/${it.file}`}
                          target="_blank"
                          rel="noreferrer"
                          className="content-mgmt-action-link"
                        >
                          Xem ma nguon
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------- Social tab ----------

function SocialTab() {
  // Phase C: 3 sub-tab structure (Connect / Posts / Analytics).
  const [subTab, setSubTab] = useState<SocialSubTab>('connect');
  const [channel, setChannel] = useState('facebook');
  const [sort, setSort] = useState<'reach_desc' | 'engagement_desc' | 'posted_desc'>('posted_desc');

  const channelsState = useAdminContent<ChannelsResponse>('/social/channels');
  const fbQuery = useMemo(() => `?limit=50&sort=${sort}`, [sort]);
  const fbState = useFbPosts(fbQuery);

  const activeChannel = channelsState.data?.channels.find((c) => c.id === channel);
  const isLive = activeChannel?.status === 'live';

  return (
    <section className="content-mgmt-tab-body">
      {/* Phase C: sub-tab nav (Connect / Posts / Analytics). */}
      <nav className="social-subtab-nav" aria-label="Social sub-tab">
        <button
          type="button"
          className={`social-subtab ${subTab === 'connect' ? 'social-subtab-active' : ''}`}
          onClick={() => setSubTab('connect')}
          aria-pressed={subTab === 'connect'}
        >
          Ket noi tai khoan
        </button>
        <button
          type="button"
          className={`social-subtab ${subTab === 'posts' ? 'social-subtab-active' : ''}`}
          onClick={() => setSubTab('posts')}
          aria-pressed={subTab === 'posts'}
        >
          Bai post (FB)
        </button>
        <button
          type="button"
          className={`social-subtab ${subTab === 'analytics' ? 'social-subtab-active' : ''}`}
          onClick={() => setSubTab('analytics')}
          aria-pressed={subTab === 'analytics'}
        >
          Phan tich cross-platform
        </button>
      </nav>

      {subTab === 'connect' && (
        <>
          {/* MD-30 Phase B: Connect cards 4 nen tang OAuth (per F-MK-SOCIAL-MULTIPLATFORM AC-10). */}
          <SocialConnectCards />
        </>
      )}

      {subTab === 'analytics' && <SocialAnalyticsPanel />}

      {subTab === 'posts' && (
        <>
      <div className="content-mgmt-filter-bar" style={{ marginTop: 24 }}>
        <label className="content-mgmt-filter">
          <span className="content-mgmt-filter-label">Kenh</span>
          <select value={channel} onChange={(e) => setChannel(e.target.value)}>
            {channelsState.data?.channels.map((c) => (
              <option key={c.id} value={c.id} disabled={c.status !== 'live'}>
                {c.label} {c.status === 'live' ? '- Dang chay' : '- Sap ra mat'}
              </option>
            ))}
          </select>
        </label>

        {isLive && (
          <label className="content-mgmt-filter">
            <span className="content-mgmt-filter-label">Sap xep</span>
            <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
              <option value="posted_desc">Moi nhat</option>
              <option value="reach_desc">Tiep can cao nhat</option>
              <option value="engagement_desc">Tuong tac cao nhat</option>
            </select>
          </label>
        )}

        {isLive && (
          <button type="button" className="content-mgmt-refresh-btn" onClick={() => void fbState.refetch()}>
            Tai lai
          </button>
        )}
      </div>

      {!isLive && (
        <p className="content-mgmt-empty">
          Kenh {activeChannel?.label ?? channel} chua duoc ket noi. Phase 0 chi ho tro Facebook (Asklaw Page).
        </p>
      )}

      {isLive && fbState.loading && <p className="content-mgmt-loading">Dang tai post FB...</p>}
      {isLive && fbState.error && <p className="content-mgmt-error">Loi tai du lieu: {fbState.error}</p>}
      {isLive && !fbState.loading && !fbState.error && fbState.data && (
        <>
          <div className="content-mgmt-summary">Hien {fbState.data.posts.length}/{fbState.data.total} post.</div>

          {fbState.data.posts.length === 0 ? (
            <p className="content-mgmt-empty">Chua co post FB nao trong khoang thoi gian nay.</p>
          ) : (
            <div className="content-mgmt-table-wrap">
              <table className="content-mgmt-table">
                <thead>
                  <tr>
                    <th>Noi dung</th>
                    <th>Loai</th>
                    <th>Da dang luc</th>
                    <th>Tiep can</th>
                    <th>Tuong tac</th>
                    <th>CTR</th>
                    <th>Hanh dong</th>
                  </tr>
                </thead>
                <tbody>
                  {fbState.data.posts.map((p) => (
                    <tr key={p.fb_post_id}>
                      <td className="content-mgmt-col-caption">
                        {p.caption_snippet}
                        {p.caption_snippet.length >= 100 ? '...' : ''}
                      </td>
                      <td>{p.content_category ?? '-'}</td>
                      <td>{fmtAge(p.posted_at)}</td>
                      <td>{fmtNum(p.reach)}</td>
                      <td>{fmtPercent(p.engagement_rate)}</td>
                      <td>{fmtPercent(p.ctr ?? null)}</td>
                      <td>
                        {p.fb_post_url ? (
                          <a
                            href={p.fb_post_url}
                            target="_blank"
                            rel="noreferrer"
                            className="content-mgmt-action-link"
                          >
                            Mo FB
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/*
        Section B - Social Insight panel (merged tu MD-26 2026-05-12).
        Spec: F-A-CONTENT-MGMT.md AC-17. Chi hien khi channel = facebook (LIVE).
      */}
      {isLive && channel === 'facebook' && (
        <section className="content-mgmt-insight-section" style={{ marginTop: 32, borderTop: '1px solid #e5e7eb', paddingTop: 24 }}>
          <SocialInsightPanel scope="embedded" />
        </section>
      )}
        </>
      )}
    </section>
  );
}

// ---------- Main ----------

function ContentInner() {
  const [tab, setTab] = useState<TabId>('blog');

  return (
    <main className="dashboard-shell content-mgmt-shell">
      <header className="content-mgmt-header">
        <h1 className="content-mgmt-title">Quan ly noi dung</h1>
        <p className="content-mgmt-sub">
          Theo doi blog article va post FB Page Asklaw. Phase 0 chi xem, chua sua tu UI.
        </p>
      </header>

      <nav className="content-mgmt-tabs" aria-label="Tab noi dung">
        <button
          type="button"
          className={`content-mgmt-tab ${tab === 'blog' ? 'content-mgmt-tab-active' : ''}`}
          onClick={() => setTab('blog')}
          aria-pressed={tab === 'blog'}
        >
          Blog (bai viet)
        </button>
        <button
          type="button"
          className={`content-mgmt-tab ${tab === 'social' ? 'content-mgmt-tab-active' : ''}`}
          onClick={() => setTab('social')}
          aria-pressed={tab === 'social'}
        >
          Social (FB Page)
        </button>
      </nav>

      {tab === 'blog' && <BlogTab />}
      {tab === 'social' && <SocialTab />}

      <footer className="content-mgmt-footer">
        Du lieu noi bo. Phase 0 chi danh cho founder va admin. UPL canh bao chi mang tinh chi bao,
        khong thay the kiem duyet con nguoi cua legal-upl.
      </footer>
    </main>
  );
}

export function DashboardContentRoute() {
  return (
    <DashboardGate>
      <DashboardLayout>
        <ContentInner />
      </DashboardLayout>
    </DashboardGate>
  );
}
